import { useNavigate } from 'react-router-dom'

import {
  F0Dialog,
  F0Form,
  f0FormField,
  useF0Form,
  useF0FormDefinition,
} from '@factorialco/f0-react'
import {
  CreateProcurementTaskFormDocument,
  TaskFormsDataCollectionDocument,
} from 'generated/resources/graphql'
import { z } from 'zod'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

interface Props {
  onClose: () => void
}

const CreateTaskFormModal = ({ onClose }: Props) => {
  const { company } = useCurrentSession()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [createProcurementTaskForm] = useMutation(CreateProcurementTaskFormDocument, {
    extractorFn: (data) => data.procurementMutations.createProcurementTaskForm,
  })

  const { formRef, submit, isSubmitting, hasErrors } = useF0Form()

  const schema = z.object({
    name: f0FormField(z.string().trim().min(1), {
      label: i18n.t('procurement.purchase_requests.settings.task_forms.create.labels.name'),
    }),
  })

  const formDefinition = useF0FormDefinition({
    name: 'create-task-form',
    schema,
    defaultValues: { name: '' },
    onSubmit: async ({ data }) => {
      const taskFormResult = await createProcurementTaskForm({
        variables: {
          name: data.name,
          category: 'basic',
          companyId: company.id,
        },
        refetchQueries: [TaskFormsDataCollectionDocument],
      })

      let fieldErrors: Record<string, string> = {}
      const handled = handleResult(taskFormResult, (errors) => {
        fieldErrors = errors
      })

      if (!handled.success) {
        return { success: false, errors: fieldErrors }
      }

      const taskFormId = handled.data?.taskForm?.id
      if (!taskFormId) return { success: false }

      onClose()
      navigate(navigation.settings.procurement.taskForms.id.edit.fullPath({ id: taskFormId }))

      return { success: true }
    },
    submitConfig: { type: 'default', hideSubmitButton: true },
  })

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('procurement.purchase_requests.settings.task_forms.create.title')}
      description={i18n.t('procurement.purchase_requests.settings.task_forms.create.description')}
      primaryAction={{
        label: i18n.t('procurement.purchase_requests.settings.task_forms.create.buttons.save'),
        onClick: submit,
        loading: isSubmitting,
        disabled: hasErrors,
      }}
      secondaryAction={{
        label: i18n.t('procurement.purchase_requests.settings.task_forms.create.buttons.cancel'),
        onClick: onClose,
      }}
    >
      <F0Form formRef={formRef} formDefinition={formDefinition} />
    </F0Dialog>
  )
}

export default CreateTaskFormModal
